"use client";
/**
 * PostHog provider organism initializing analytics SDK with /ingest proxy.
 */
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useLayoutEffect } from "react";
import { syncFlagOverridesFromUrl, useFeatureFlag, FEATURE_FLAGS } from "@/lib/analytics/featureFlags";
import { isNonProductionAnalyticsHost } from "@/lib/analytics/posthogHostEnvironment";
import { scrubClaimTokenFromProperties } from "@/lib/onboarding/urlScrub";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Persist a `?flags=` override (e.g. ?flags=roadmap) into a cookie so it survives
  // navigation and rides on API requests — the server honors it too. Allowlisted
  // flags only (see @/lib/featureFlags/overrides).
  useLayoutEffect(() => {
    syncFlagOverridesFromUrl();
  }, []);

  useLayoutEffect(() => {
    // Initialize only when a key is provided
    if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: "/ingest",
        ui_host: "https://us.posthog.com",
        defaults: "2025-05-24",
        capture_pageview: false,
        // Avoid injecting external scripts during/around hydration
        // to prevent SSR/CSR HTML mismatches
        capture_exceptions: false,
        disable_surveys: false,
        disable_surveys_automatic_display: true,
        // Silence PostHog console noise by default; opt-in with NEXT_PUBLIC_POSTHOG_DEBUG=true
        debug:
          process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "true" ||
          process.env.NEXT_PUBLIC_POSTHOG_DEBUG_SESSION_RECORDING === "true",
        // Optional noise reducers (keep simple, typed-safe defaults)
        enable_heatmaps: process.env.NEXT_PUBLIC_POSTHOG_HEATMAPS === "true",
        // Disable console log recording
        enable_recording_console_log: false,
        // Disable autocapture to reduce noise
        autocapture: false,
        // Strip the onboarding claim token (?oc=, incl. encoded inside
        // redirect_url) from captured URLs — $current_url/$referrer on the
        // /sign-up page would otherwise retain a live claim credential for
        // the analytics retention window (ADR 041). before_send, not the
        // deprecated sanitize_properties (which logs an error per event on
        // posthog-js 1.387).
        before_send: (event) => {
          if (event?.properties) scrubClaimTokenFromProperties(event.properties);
          if (event?.$set) scrubClaimTokenFromProperties(event.$set);
          if (event?.$set_once) scrubClaimTokenFromProperties(event.$set_once);
          return event;
        },
        // Only enable session recordings in production (not staging/preview or local dev)
        // Override with NEXT_PUBLIC_POSTHOG_DEBUG_SESSION_RECORDING=true for local testing
        // Next.js inlines NEXT_PUBLIC_* / NODE_ENV only on literal property reads — passing
        // process.env as an object yields an empty object in the browser bundle (LEV-1693).
        disable_session_recording:
          process.env.NEXT_PUBLIC_POSTHOG_DEBUG_SESSION_RECORDING === "true"
            ? false
            : isNonProductionAnalyticsHost({
                NODE_ENV: process.env.NODE_ENV,
                NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
              }),
        // Allow the staging test bot (Playwright/HeadlessChrome) to send events.
        // SDK stamps $browser_type:"bot" so dashboards can still separate bot traffic.
        // Production keeps the default filter — no bot traffic expected there (LEV-1693).
        opt_out_useragent_filter: isNonProductionAnalyticsHost({
          NODE_ENV: process.env.NODE_ENV,
          NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
        }),
      });
    }
  }, []);

  return (
    <PHProvider client={posthog}>
      <ExperimentFlagTracker />
      {children}
    </PHProvider>
  );
}

const ALL_FLAG_KEYS = Object.values(FEATURE_FLAGS);

/** Evaluates every active flag so the JS SDK enriches subsequent captures with $feature/* event properties. */
function ExperimentFlagTracker() {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- ALL_FLAG_KEYS is static (module-level const)
  ALL_FLAG_KEYS.forEach((flag) => useFeatureFlag(flag));
  return null;
}
